import { buildEmbedQuery, type EmbedLayout, type EmbedTheme } from './params';

/**
 * Fallback frame heights (px) for when the host page blocks or lacks
 * embed.js, which otherwise sizes the frame to the player. Measured on one
 * tone at ~500px wide with the live tab showing and, for compact, the panel
 * open (it opens itself when the host can't resize); a little headroom is
 * included because narrow frames wrap the Input/Reverb row.
 */
export const EMBED_FALLBACK_HEIGHT: Record<EmbedLayout, number> = {
  full: 380,
  compact: 340,
};

export interface EmbedSnippetOptions {
  /** Site origin, e.g. `https://www.tone3000.com`. */
  origin: string;
  toneIds: number[];
  layout: EmbedLayout;
  /** Show the "Plug in and play" tab. Drops `microphone` / `speaker-selection` from `allow` when off. */
  live: boolean;
  theme: EmbedTheme;
  /** Accessible frame title; falls back to "TONE3000 player". */
  title?: string | null;
  /** Optional attribution tag, echoed in embed analytics. */
  ref?: string;
}

const escapeAttr = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// The URL is ours (ids and fixed keywords), so a raw `&` between parameters
// is safe and reads better than `&amp;` to the people pasting it.
const escapeUrlAttr = (s: string) =>
  s.replace(/"/g, '%22').replace(/</g, '%3C').replace(/>/g, '%3E');

/** URL of the page the iframe loads. */
export function embedPlayerUrl(
  opts: Pick<EmbedSnippetOptions, 'origin' | 'toneIds' | 'layout' | 'live' | 'theme' | 'ref'>
) {
  return `${opts.origin}/embed/player?${buildEmbedQuery(opts)}`;
}

/**
 * The HTML a user pastes into their site. The host writes the <iframe>
 * itself (works without JavaScript and inside CMS blocks that strip scripts);
 * embed.js is optional and only sizes the frame and relays player events.
 */
export function buildEmbedSnippet(opts: EmbedSnippetOptions): string {
  const src = embedPlayerUrl(opts);
  const title = opts.title?.trim() ? `TONE3000 player: ${opts.title.trim()}` : 'TONE3000 player';
  // speaker-selection: Chromium gates setSinkId on it inside cross-origin
  // frames, which the player's Output Device picker needs.
  const allow = opts.live ? 'autoplay; microphone; speaker-selection' : 'autoplay';
  const height = EMBED_FALLBACK_HEIGHT[opts.layout];
  return [
    `<iframe src="${escapeUrlAttr(src)}" data-t3k-player title="${escapeAttr(title)}" width="100%" height="${height}" style="border:0;display:block" allow="${allow}" loading="lazy"></iframe>`,
    `<script async src="${opts.origin}/embed.js"></script>`,
  ].join('\n');
}
